'use client';

import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button } from '@repo/design-system/components/ds/button';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Modal } from '@repo/design-system/components/ui/modal';
import { TextArea } from '@repo/design-system/components/ui/text-area';
import { TextInput } from '@repo/design-system/components/ui/text-input';
import { toast } from '@repo/design-system/components/ui/toaster';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { DESCRIPTION_MAX_LENGTH, type SavedChecklist, TITLE_MAX_LENGTH } from '@repo/map-user-data/types';
import { useCreateSavedChecklistMutation } from '@repo/store/maps/api/mapsApi';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectAuthToken } from '@repo/store/maps/slices/auth';
import { selectMapName, selectTitleName } from '@repo/store/maps/slices/mapData';
import type { SavedChecklistSource } from '@repo/types/analytic-events/maps-analytic-events';
import { useEffect, useRef, useState } from 'react';
export type SaveChecklistSource = SavedChecklistSource;
export interface SaveChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-filled title. Typically "Near {anchorTitle}" when opened from Explore. */
  defaultTitle: string;
  /** Pre-filled description (default empty string). */
  defaultDescription?: string;
  /** Snapshot of pin ids to persist with the checklist. */
  pinIds: string[];
  mapExternalId: string;
  /** Where this save was triggered from. Included in analytics events. */
  source: SaveChecklistSource;
  /** Hero action this save originated from. Set when `source` is 'hero_action' to attribute the conversion. */
  heroActionId?: string;
  heroActionName?: string;
  /** Optional callback fired with the newly-created checklist on success. */
  onSaved?: (checklist: SavedChecklist) => void;
}
export const SaveChecklistModal = ({
  isOpen,
  onClose,
  defaultTitle,
  defaultDescription = '',
  pinIds,
  mapExternalId,
  source,
  heroActionId,
  heroActionName,
  onSaved
}: SaveChecklistModalProps) => {
  const {
    t
  } = useT(['game_map']);
  const analytics = useAnalytics();
  const hostType = useHostType();
  const token = useAppSelector(selectAuthToken);
  const mapName = useAppSelector(selectMapName);
  const titleName = useAppSelector(selectTitleName);
  const [createChecklist, {
    isLoading
  }] = useCreateSavedChecklistMutation();
  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState(defaultDescription);
  const [titleError, setTitleError] = useState<string | undefined>(undefined);
  const [descriptionError, setDescriptionError] = useState<string | undefined>(undefined);
  const titleRef = useRef<HTMLInputElement>(null);
  // Track whether a save completed in the current open session so cancel doesn't fire after success.
  const savedInSessionRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on open, not when stale default/source values change
  useEffect(() => {
    if (!isOpen) return;
    setTitle(defaultTitle);
    setDescription(defaultDescription);
    setTitleError(undefined);
    setDescriptionError(undefined);
    savedInSessionRef.current = false;
    if (analytics && hostType) {
      analytics.event('game_map_saved_checklist_save_open', {
        mapId: mapExternalId,
        mapName,
        titleName,
        platform: hostType,
        source,
        pinCount: pinIds.length,
        ...(heroActionId ? {
          heroActionId
        } : {}),
        ...(heroActionName ? {
          heroActionName
        } : {})
      });
    }
    requestAnimationFrame(() => {
      titleRef.current?.focus();
      titleRef.current?.select();
    });
  }, [isOpen]);
  const handleClose = () => {
    if (!savedInSessionRef.current && analytics && hostType) {
      analytics.event('game_map_saved_checklist_save_cancel', {
        mapId: mapExternalId,
        mapName,
        titleName,
        platform: hostType,
        source,
        ...(heroActionId ? {
          heroActionId
        } : {}),
        ...(heroActionName ? {
          heroActionName
        } : {})
      });
    }
    onClose();
  };
  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    let hasError = false;
    if (trimmedTitle.length === 0) {
      setTitleError(t($ => $.save_checklist.give_it_a_name));
      hasError = true;
    } else if (trimmedTitle.length > TITLE_MAX_LENGTH) {
      setTitleError(t($_0 => $_0.save_checklist.title_must_be_100_characters_or_fewer));
      hasError = true;
    } else {
      setTitleError(undefined);
    }
    if (description.length > DESCRIPTION_MAX_LENGTH) {
      setDescriptionError(t($_1 => $_1.save_checklist.description_must_be_500_characters_or_fewer));
      hasError = true;
    } else {
      setDescriptionError(undefined);
    }
    if (hasError) return;
    try {
      const result = await createChecklist({
        mapExternalId,
        input: {
          title: trimmedTitle,
          description: trimmedDescription,
          pinIds
        },
        token: token ?? ''
      }).unwrap();
      savedInSessionRef.current = true;
      if (analytics && hostType) {
        analytics.event('game_map_saved_checklist_save_submit', {
          mapId: mapExternalId,
          mapName,
          titleName,
          platform: hostType,
          source,
          pinCount: pinIds.length,
          hasDescription: trimmedDescription.length > 0,
          titleLength: trimmedTitle.length,
          ...(heroActionId ? {
            heroActionId
          } : {}),
          ...(heroActionName ? {
            heroActionName
          } : {})
        });
      }
      toast({
        description: t($_3 => $_3.save_checklist.checklist_saved)
      });
      onSaved?.(result);
      onClose();
    } catch {
      toast({
        description: t($_2 => $_2.save_checklist.couldnt_save_checklist),
        variant: 'error'
      });
    }
  };
  const trimmedTitle_0 = title.trim();
  const canSubmit = trimmedTitle_0.length > 0 && !!token && !isLoading;
  return <Modal isOpen={isOpen} onOpenChange={details => {
    if (!details.open) handleClose();
  }} title={t($_4 => $_4.save_checklist.save_checklist)} size="sm">
            <form onSubmit={handleSubmit}>
                <FlexColumn gap="12">
                    <TextInput ref={titleRef} label={t($_5 => $_5.save_checklist.title)} placeholder={t($_6 => $_6.save_checklist.give_it_a_name)} value={title} onChange={e_0 => setTitle(e_0.target.value)} fullWidth required errorText={titleError} />
                    <TextArea label={t($_7 => $_7.save_checklist.description)} placeholder={t($_8 => $_8.save_checklist.optional_notes)} value={description} onChange={e_1 => setDescription(e_1.target.value)} errorText={descriptionError} fullWidth />
                    <FlexRow gap="8" justify="end" width="full">
                        <Button type="button" onClick={handleClose} color="secondary" size="lg">
                            {t($_9 => $_9.save_checklist.cancel)}
                        </Button>
                        <Button type="submit" color="primary" size="lg" disabled={!canSubmit}>
                            {t($_10 => $_10.save_checklist.save)}
                        </Button>
                    </FlexRow>
                </FlexColumn>
            </form>
        </Modal>;
};